import { useState, useEffect, useMemo, Fragment } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { ShieldCheck, RotateCcw, Save, Loader2, Lock, Check } from 'lucide-react';

const getErr = (e) => e?.response?.data?.detail || 'Something went wrong';

export default function Rbac() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});          // role.value -> Set(cap values)
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/admin/rbac');
      setData(res.data);
      const d = {};
      Object.entries(res.data.matrix).forEach(([r, caps]) => { d[r] = new Set(caps); });
      setDraft(d);
    } catch (e) { toast.error(getErr(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const editableRoles = useMemo(() => (data?.roles || []).filter(r => r.editable), [data]);

  const toggle = (roleVal, capVal) => {
    setDraft(prev => {
      const next = new Set(prev[roleVal]);
      next.has(capVal) ? next.delete(capVal) : next.add(capVal);
      return { ...prev, [roleVal]: next };
    });
  };

  const dirty = (roleVal) => {
    if (!data) return false;
    const orig = new Set(data.matrix[roleVal] || []);
    const cur = draft[roleVal] || new Set();
    if (orig.size !== cur.size) return true;
    for (const c of cur) if (!orig.has(c)) return true;
    return false;
  };

  const saveRole = async (roleVal) => {
    setSavingRole(roleVal);
    try {
      const res = await api.put(`/admin/rbac/${roleVal}`, { capabilities: [...(draft[roleVal] || [])] });
      setData(res.data);
      const d = {};
      Object.entries(res.data.matrix).forEach(([r, caps]) => { d[r] = new Set(caps); });
      setDraft(d);
      toast.success('Permissions updated');
    } catch (e) { toast.error(getErr(e)); }
    setSavingRole(null);
  };

  const resetToDefault = async (roleVal) => {
    // reset one role = set its caps back to the code default, then save
    if (!window.confirm('Reset this role to built-in defaults?')) return;
    setDraft(prev => ({ ...prev, [roleVal]: new Set(data.defaults[roleVal] || []) }));
    setSavingRole(roleVal);
    try {
      const res = await api.put(`/admin/rbac/${roleVal}`, { capabilities: [...(data.defaults[roleVal] || [])] });
      setData(res.data);
      const d = {};
      Object.entries(res.data.matrix).forEach(([r, caps]) => { d[r] = new Set(caps); });
      setDraft(d);
      toast.success('Role reset to defaults');
    } catch (e) { toast.error(getErr(e)); }
    setSavingRole(null);
  };

  const resetAll = async () => {
    if (!window.confirm('Reset ALL roles to built-in defaults? This clears every customization.')) return;
    setResetting(true);
    try {
      const res = await api.post('/admin/rbac/reset');
      setData(res.data);
      const d = {};
      Object.entries(res.data.matrix).forEach(([r, caps]) => { d[r] = new Set(caps); });
      setDraft(d);
      toast.success('All roles reset to defaults');
    } catch (e) { toast.error(getErr(e)); }
    setResetting(false);
  };

  if (loading) {
    return <div className="p-12 text-center text-slate-400">
      <Loader2 className="animate-spin mx-auto mb-3" /> Loading permissions…
    </div>;
  }
  if (!data) return <div className="p-12 text-center text-slate-400">Failed to load.</div>;

  const roleCols = data.roles; // includes admin (locked) + editable roles

  return (
    <div className="space-y-5" data-testid="rbac-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl flex items-center gap-2">
            <ShieldCheck size={20} className="text-slate-500" /> Roles & Permissions
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Configure what each role can do. Admin is always full-access. Changes apply immediately.
          </p>
        </div>
        <button onClick={resetAll} disabled={resetting} data-testid="rbac-reset-all"
          className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-sm text-slate-600 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50">
          {resetting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Reset all to defaults
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-auto max-h-[70vh] shadow-sm">
        <table className="w-full text-sm border-collapse sticky-head">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left font-semibold px-4 py-3 text-slate-600 sticky left-0 bg-slate-50 min-w-[260px]">Capability</th>
              {roleCols.map(r => (
                <th key={r.value} className="px-4 py-3 text-center font-semibold text-slate-700 min-w-[130px]">
                  <div className="flex items-center justify-center gap-1">
                    {!r.editable && <Lock size={11} className="text-slate-400" />}
                    {r.label}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.capabilities.map(group => (
              <Fragment key={group.group}>
                <tr className="bg-slate-100/70">
                  <td colSpan={roleCols.length + 1} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    {group.group}
                  </td>
                </tr>
                {group.items.map(cap => (
                  <tr key={cap.value} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-2 text-slate-700 sticky left-0 bg-white">{cap.label}</td>
                    {roleCols.map(r => {
                      const on = r.always_all || (draft[r.value]?.has(cap.value));
                      const disabled = !r.editable;
                      return (
                        <td key={r.value} className="px-4 py-2 text-center">
                          <button
                            onClick={() => !disabled && toggle(r.value, cap.value)}
                            disabled={disabled}
                            data-testid={`cap-${r.value}-${cap.value}`}
                            aria-pressed={!!on}
                            className={`w-5 h-5 rounded-sm border flex items-center justify-center mx-auto transition-colors ${
                              on ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-300'
                            } ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-emerald-400 cursor-pointer'}`}>
                            {on && <Check size={13} />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-role save bar */}
      <div className="flex flex-wrap gap-3">
        {editableRoles.map(r => (
          <div key={r.value} className={`flex items-center gap-2 border rounded-sm px-3 py-2 ${
            dirty(r.value) ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white'}`}>
            <span className="text-sm font-semibold text-slate-700">{r.label}</span>
            {dirty(r.value) && <span className="text-[11px] text-orange-600 font-semibold">unsaved</span>}
            <button onClick={() => resetToDefault(r.value)} disabled={savingRole === r.value}
              className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1">Default</button>
            <button onClick={() => saveRole(r.value)} disabled={!dirty(r.value) || savingRole === r.value}
              data-testid={`rbac-save-${r.value}`}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-xs font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-40">
              {savingRole === r.value ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        Note: Admin always retains every permission (so no one can lock themselves out). Permission changes
        take effect on the user's next action; they may need to refresh.
      </p>
    </div>
  );
}
